import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';

const ConfigContext = createContext();

export const useConfig = () => {
  const context = useContext(ConfigContext);
  if (!context) {
    throw new Error('useConfig must be used within a ConfigProvider');
  }
  return context;
};

export const ConfigProvider = ({ children }) => {
  const [config, setConfig] = useState({
    appName: 'SnowTrack™',
    appTagline: 'Route Management for Snow & Lawn Pros',
    theme: {
      primaryColor: '#1e88e5',
      primaryDark: '#1565c0',
      primaryLight: '#64b5f6',
      secondaryColor: '#ff9800',
      secondaryDark: '#f57c00',
      secondaryLight: '#ffb74d',
      successColor: '#4caf50',
      warningColor: '#ff9800',
      errorColor: '#f44336',
      textColor: '#333333',
      textLight: '#666666',
      backgroundColor: '#f5f5f5',
      cardBackground: '#ffffff',
      borderColor: '#e0e0e0',
    },
    serviceTypes: {},
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadConfig();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadConfig = async () => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/config/public`);
      setConfig(response.data);
      applyTheme(response.data.theme);
    } catch (err) {
      console.error('Failed to load config:', err);
      // Use default config if API fails
      applyTheme(config.theme);
    } finally {
      setLoading(false);
    }
  };

  const applyTheme = (theme) => {
    const root = document.documentElement;

    // Set CSS custom properties
    root.style.setProperty('--color-primary', theme.primaryColor);
    root.style.setProperty('--color-primary-dark', theme.primaryDark);
    root.style.setProperty('--color-primary-light', theme.primaryLight);
    root.style.setProperty('--color-secondary', theme.secondaryColor);
    root.style.setProperty('--color-secondary-dark', theme.secondaryDark);
    root.style.setProperty('--color-secondary-light', theme.secondaryLight);
    root.style.setProperty('--color-success', theme.successColor);
    root.style.setProperty('--color-warning', theme.warningColor);
    root.style.setProperty('--color-error', theme.errorColor);
    root.style.setProperty('--color-text', theme.textColor);
    root.style.setProperty('--color-text-light', theme.textLight);
    root.style.setProperty('--color-background', theme.backgroundColor);
    root.style.setProperty('--color-card', theme.cardBackground);
    root.style.setProperty('--color-border', theme.borderColor);
  };

  const getAppName = () => config.appName;
  const getAppTagline = () => config.appTagline;
  const getTheme = () => config.theme;
  const getServiceTypes = () => config.serviceTypes;

  const value = {
    config,
    loading,
    getAppName,
    getAppTagline,
    getTheme,
    getServiceTypes,
    refreshConfig: loadConfig,
  };

  return (
    <ConfigContext.Provider value={value}>
      {children}
    </ConfigContext.Provider>
  );
};
